import { useMemo, useState } from 'react';
import { Button, Field, Input, Panel, ScreenHeader, StateBox } from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import type { PortalApi } from '../data/types.js';
import { Drop } from '../components/Drop.js';
import { IdentifierField } from '../components/IdentifierField.js';
import { ACTIVE_VERTICAL } from '../config/verticals.js';

function GroupHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="eyebrow-sm" style={{ marginBottom: 12 }}>
      {children}
    </div>
  );
}

export function Submit({ api, onSubmitted }: { api: PortalApi; onSubmitted: () => void }) {
  const inv = useAsync(() => api.getInvitation());
  const slots = useAsync(() => api.getUploadSlots());
  const [uploads, setUploads] = useState<ReadonlySet<string>>(new Set());

  const required = useMemo(() => (slots.data ?? []).filter((s) => s.required), [slots.data]);
  const ready = required.length > 0 && required.every((s) => uploads.has(s.key));

  if (inv.loading || slots.loading) return <StateBox kind="loading" title="Loading your claim form" />;
  if (inv.error || !inv.data || slots.error || !slots.data)
    return <StateBox kind="error" title="Could not load the form">{inv.error ?? slots.error}</StateBox>;

  const up = (k: string): void =>
    setUploads((prev) => {
      const next = new Set(prev);
      next.add(k);
      return next;
    });

  const V = ACTIVE_VERTICAL;
  return (
    <div className="screen">
      <ScreenHeader
        eyebrow="Submit claim"
        title={V.portal_copy.submit_heading}
        lead="Each field and document is a claim Garnet verifies against the registers. Confirm the identifier, then attach the required evidence."
      />

      <Panel className="pad">
        <GroupHead>Counterparty</GroupHead>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Register no.">
            <Input mono defaultValue={inv.data.register_id} aria-label="Register number" />
          </Field>
          <Field label="Role in deal">
            <Input defaultValue={inv.data.role} aria-label="Role in deal" />
          </Field>
        </div>
      </Panel>

      <Panel className="pad">
        <GroupHead>Asset identifier</GroupHead>
        <IdentifierField defaultValue="(01)05012345678900(17)271231(10)LOT42(21)SN0007" />
      </Panel>

      <Panel className="pad">
        <GroupHead>Evidence · {slots.data.length} documents</GroupHead>
        <div style={{ display: 'grid', gap: 10 }}>
          {slots.data.map((s) => (
            <Drop key={s.key} label={s.label} hint={s.hint} required={s.required} done={uploads.has(s.key)} onDrop={() => up(s.key)} />
          ))}
        </div>
      </Panel>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="primary" icon="upload" disabled={!ready} onClick={onSubmitted}>
          Submit for verification
        </Button>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
          {ready ? 'ready' : `${required.map((s) => s.label).join(' and ')} required`}
        </span>
      </div>
    </div>
  );
}
